// TDEE and macro calculations using Mifflin-St Jeor equation

export interface TDEEParams {
  weight: number; // in lbs
  height: string; // e.g., "5'10" or "178 cm"
  age: number;
  sex: 'male' | 'female';
  activityLevel: 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';
}

export interface NutritionTargets {
  calories: number;
  protein: number; // grams
  carbs: number; // grams
  fat: number; // grams
}

export type NutritionGoalType = 'lose' | 'maintain' | 'gain';

const ACTIVITY_MULTIPLIERS = {
  sedentary: 1.2,      // Little/no exercise
  light: 1.375,        // Light exercise 1-3 days/week
  moderate: 1.55,      // Moderate exercise 3-5 days/week
  active: 1.725,       // Hard exercise 6-7 days/week
  very_active: 1.9,    // Very hard exercise, physical job
};

// Convert height string to cm
export function parseHeightToCm(height: string): number {
  if (!height) return 170; // default
  
  // Check for feet/inches format (e.g., "5'10" or "5'10\"" or "5 ft 10 in")
  const feetInchMatch = height.match(/(\d+)'?\s*(\d+)?/);
  if (feetInchMatch) {
    const feet = parseInt(feetInchMatch[1]) || 0;
    const inches = parseInt(feetInchMatch[2]) || 0;
    return Math.round((feet * 30.48) + (inches * 2.54));
  }
  
  // Check for cm format
  const cmMatch = height.match(/(\d+)\s*cm/i);
  if (cmMatch) {
    return parseInt(cmMatch[1]);
  }
  
  // Just a number - assume cm if > 100, otherwise feet
  const num = parseFloat(height);
  if (!isNaN(num)) {
    return num > 100 ? num : num * 30.48;
  }
  
  return 170; // default
}

// Convert lbs to kg
function lbsToKg(lbs: number): number {
  return lbs * 0.453592;
}

// Calculate BMR using Mifflin-St Jeor equation
export function calculateBMR(params: TDEEParams): number {
  const weightKg = lbsToKg(params.weight);
  const heightCm = parseHeightToCm(params.height);
  
  if (params.sex === 'male') {
    return (10 * weightKg) + (6.25 * heightCm) - (5 * params.age) + 5;
  } else {
    return (10 * weightKg) + (6.25 * heightCm) - (5 * params.age) - 161;
  }
}

// Calculate TDEE (Total Daily Energy Expenditure)
export function calculateTDEE(params: TDEEParams): number {
  const bmr = calculateBMR(params);
  const multiplier = ACTIVITY_MULTIPLIERS[params.activityLevel] || ACTIVITY_MULTIPLIERS.moderate;
  return Math.round(bmr * multiplier);
}

// Calculate daily calorie target based on goal
export function calculateCalorieTarget(tdee: number, goal: NutritionGoalType): number {
  switch (goal) {
    case 'lose':
      return Math.round(tdee - 500); // ~1 lb/week loss
    case 'gain':
      return Math.round(tdee + 300); // lean bulk
    case 'maintain':
    default:
      return tdee;
  }
}

// Calculate macro targets based on goal and calories
export function calculateMacroTargets(
  calories: number,
  weightLbs: number,
  goal: NutritionGoalType
): NutritionTargets {
  const weightKg = lbsToKg(weightLbs);
  
  let proteinMultiplier: number;
  let fatPercentage: number;
  
  switch (goal) {
    case 'lose':
      // Higher protein for muscle preservation during cut
      proteinMultiplier = 2.2; // 1g per lb
      fatPercentage = 0.25;
      break;
    case 'gain':
      // Moderate-high protein for muscle building
      proteinMultiplier = 1.8; // 0.8g per lb
      fatPercentage = 0.25;
      break;
    case 'maintain':
    default:
      proteinMultiplier = 1.6; // 0.7g per lb
      fatPercentage = 0.30;
      break;
  }
  
  const protein = Math.round(weightKg * proteinMultiplier);
  const fat = Math.round((calories * fatPercentage) / 9); // 9 cal per gram fat
  const proteinCals = protein * 4;
  const fatCals = fat * 9;
  const carbs = Math.round((calories - proteinCals - fatCals) / 4); // remaining from carbs
  
  return {
    calories,
    protein,
    carbs: Math.max(carbs, 50), // minimum carbs
    fat,
  };
}

// Full calculation from user profile
export function calculateNutritionTargets(
  params: TDEEParams,
  goal: NutritionGoalType
): NutritionTargets {
  const tdee = calculateTDEE(params);
  const calorieTarget = calculateCalorieTarget(tdee, goal);
  return calculateMacroTargets(calorieTarget, params.weight, goal);
}

// Get activity level label
export function getActivityLevelLabel(level: string): string {
  const labels: Record<string, string> = {
    sedentary: 'Sedentary (little/no exercise)',
    light: 'Light (1-3 days/week)',
    moderate: 'Moderate (3-5 days/week)',
    active: 'Active (6-7 days/week)',
    very_active: 'Very Active (intense daily)',
  };
  return labels[level] || labels.moderate;
}
